import aiqBiSvg from '@/assets/aiq-bi.svg'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { learnerApi } from '@/api/b2b'
import { aiqApi, AIQStatus } from '@/api/aiq'
import { productVideoActivityApi } from '@/api/productVideoActivity'
import { getEffectiveConfigurationForB2B } from '@/api/configuration'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { isDevToolsVisible } from '@/utils/devTools'
import { isAiqReportDateLocked } from '@/config/aiqReportGate'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@quebon/ui/components/dialog'
import {
  Brain,
  TrendingUp,
  Lock,
  FileText,
  Play,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Wrench,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  GitCompare,
  Calendar,
  ShieldOff,
} from 'lucide-react'

// Post-AIQ 시작 가능 날짜 (KST)
const POST_AIQ_START_DATE = new Date('2026-04-23T00:00:00+09:00')

// ============================================================================
// Sub Components
// ============================================================================

interface PreAIQCardProps {
  preTest: AIQStatus['preTest']
  classProductId: number
  onNavigate: (path: string) => void
  isVideoGated?: boolean
  onVideoGateBlocked?: () => void
}

function PreAIQCard({ preTest, classProductId, onNavigate, isVideoGated = false, onVideoGateBlocked }: PreAIQCardProps) {
  const { t } = useTranslation('learner')
  if (!preTest) return null
  const isCompleted = preTest.status === 'completed'

  return (
    <div className="flex overflow-hidden flex-col h-full bg-white rounded-xl shadow-sm">
      <div className="flex relative justify-center items-center p-6 h-32 bg-gradient-to-br from-blue-500 to-blue-600 shrink-0">
        <span className={`absolute top-3 right-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isCompleted ? 'bg-green-200 text-green-900' : 'bg-white/90 text-gray-700'}`}>
          {isCompleted ? t('aiq.completed') : t('aiq.scheduled')}
        </span>
        <div className="text-center text-white">
          <TrendingUp className="mx-auto mb-2 w-12 h-12" />
          <h3 className="text-xl font-bold">Pre-AIQ Test</h3>
        </div>
      </div>

      <div className="flex flex-col flex-1 p-6">
        <div className="flex flex-col flex-1 justify-center items-center mb-4 text-center">
          {isCompleted ? (
            <div className="p-6 w-full text-center rounded-xl bg-blue-50/70">
              <CheckCircle2 className="mx-auto mb-3 w-10 h-10 text-gray-800" />
              <p className="text-sm leading-relaxed text-gray-600 whitespace-pre-line">
                {t('aiq.preTestDesc')}
              </p>
            </div>
          ) : (
            <>
              <div className="flex justify-center items-center mb-4 w-14 h-14 bg-blue-50 rounded-xl">
                <Play className="w-7 h-7 text-blue-500" />
              </div>
              <p className="text-sm leading-relaxed text-gray-600 whitespace-pre-line">
                {t('aiq.preTestDesc')}
              </p>
            </>
          )}
        </div>

        {isCompleted && preTest.completedAt && (
          <p className="mt-auto text-xs text-right text-gray-500">
            {t('aiq.completedDate')} {new Date(preTest.completedAt).toLocaleDateString()}
          </p>
        )}

        {!isCompleted && (
          <button
            onClick={() => {
              if (isVideoGated) {
                onVideoGateBlocked?.()
              } else {
                onNavigate(`/learn/${classProductId}/aiq/pre`)
              }
            }}
            className="w-full mt-auto py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Play className="w-4 h-4" />
            {t('aiq.start')}
          </button>
        )}
      </div>
    </div>
  )
}

interface PostAIQCardProps {
  postTest: AIQStatus['postTest']
  classProductId: number
  onNavigate: (path: string) => void
  bypassDate?: boolean
}

function PostAIQCard({ postTest, classProductId, onNavigate, bypassDate }: PostAIQCardProps) {
  const { t } = useTranslation('learner')
  if (!postTest) return null
  const isLocked = postTest.status === 'locked'
  const isCompleted = postTest.status === 'completed'
  const isAvailable = postTest.status === 'available'
  const isDateLocked = !bypassDate && new Date() < POST_AIQ_START_DATE

  const badgeText = isCompleted ? t('aiq.completed') : isAvailable && !isDateLocked ? t('aiq.available') : t('aiq.scheduled')
  const badgeClass = isCompleted
    ? 'bg-green-100 text-green-800'
    : isAvailable && !isDateLocked
      ? 'bg-purple-100 text-purple-700'
      : 'bg-white/90 text-gray-700'

  return (
    <div className="flex overflow-hidden flex-col h-full bg-white rounded-xl shadow-sm">
      <div className="flex relative justify-center items-center p-6 h-32 bg-gradient-to-br from-purple-500 to-purple-600 shrink-0">
        <span className={`absolute top-3 right-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}>
          {badgeText}
        </span>
        <div className="text-center text-white">
          <Brain className="mx-auto mb-2 w-12 h-12" />
          <h3 className="text-xl font-bold">Post-AIQ Test</h3>
        </div>
      </div>

      <div className="flex flex-col flex-1 p-6">
        <div className="flex flex-col flex-1 justify-center items-center mb-4 text-center">
          {isCompleted ? (
            <div className="p-6 w-full text-center rounded-xl bg-purple-50/70">
              <CheckCircle2 className="mx-auto mb-3 w-10 h-10 text-gray-800" />
              <p className="text-sm leading-relaxed text-gray-600 whitespace-pre-line">
                {t('aiq.postTestDesc')}
              </p>
            </div>
          ) : isAvailable && !isDateLocked ? (
            <>
              <div className="flex justify-center items-center mb-4 w-14 h-14 bg-purple-50 rounded-xl">
                <Play className="w-7 h-7 text-purple-500" />
              </div>
              <p className="text-sm leading-relaxed text-gray-600 whitespace-pre-line">
                {t('aiq.postTestDesc')}
              </p>
            </>
          ) : isAvailable && isDateLocked ? (
            <>
              <div className="flex justify-center items-center mb-4 w-14 h-14 bg-purple-50/50 rounded-xl">
                <Calendar className="w-7 h-7 text-purple-400" />
              </div>
              <p className="text-sm leading-relaxed text-gray-600 whitespace-pre-line">
                {t('aiq.postTestDesc')}
              </p>
              <p className="mt-2 text-xs font-medium text-purple-600">
                {t('aiq.postDateLocked')}
              </p>
            </>
          ) : (
            <>
              <div className="flex justify-center items-center mb-4 w-14 h-14 bg-gray-100 rounded-xl">
                <Lock className="w-7 h-7 text-gray-400" />
              </div>
              <p className="text-sm leading-relaxed text-gray-600 whitespace-pre-line">
                {t('aiq.postTestDesc')}
              </p>
            </>
          )}
        </div>

        {isCompleted && postTest.completedAt && (
          <p className="mt-auto text-xs text-right text-gray-500">
            {t('aiq.completedDate')} {new Date(postTest.completedAt).toLocaleDateString()}
          </p>
        )}

        {!isCompleted && (
          isLocked || (isAvailable && isDateLocked) ? (
            <button
              disabled
              className="w-full mt-auto py-2.5 px-4 bg-gray-100 text-gray-400 rounded-lg text-sm font-medium cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isAvailable && isDateLocked ? <Calendar className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {isAvailable && isDateLocked ? t('aiq.postDateShort') : t('aiq.start')}
            </button>
          ) : (
            <button
              onClick={() => onNavigate(`/learn/${classProductId}/aiq/post`)}
              className="w-full mt-auto py-2.5 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              <Play className="w-4 h-4" />
              {t('aiq.start')}
            </button>
          )
        )}
      </div>
    </div>
  )
}

interface AIQReportCardProps {
  report: AIQStatus['report']
  classProductId: number
  onNavigate: (path: string) => void
  bypassReportFlag?: boolean
}

function AIQReportCard({ report, classProductId, onNavigate, bypassReportFlag }: AIQReportCardProps) {
  const { t } = useTranslation('learner')
  if (!report) return null
  const isReportReady = report.status === 'available'
  const isDateLocked = !bypassReportFlag && isAiqReportDateLocked()
  const isAvailable = isReportReady && (report.reportEnabled || bypassReportFlag) && !isDateLocked

  const badgeText = isAvailable ? t('aiq.reportAvailable') : isReportReady ? t('aiq.reportPreparing') : t('aiq.scheduled')
  const badgeClass = isAvailable
    ? 'bg-green-100 text-green-800'
    : 'bg-white/90 text-gray-700'

  return (
    <div className="flex overflow-hidden flex-col h-full bg-white rounded-xl shadow-sm">
      <div className="flex relative justify-center items-center p-6 h-32 bg-gradient-to-br from-green-500 to-teal-500 shrink-0">
        <span className={`absolute top-3 right-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}>
          {badgeText}
        </span>
        <div className="text-center text-white">
          <FileText className="mx-auto mb-2 w-12 h-12" />
          <h3 className="text-xl font-bold">{t('aiq.reportTitle')}</h3>
        </div>
      </div>

      <div className="flex flex-col flex-1 p-6">
        <div className="flex flex-col flex-1 justify-center items-center mb-4 text-center">
          {isAvailable ? (
            <>
              <div className="flex justify-center items-center mb-4 w-14 h-14 bg-green-50 rounded-xl">
                <FileText className="w-7 h-7 text-green-500" />
              </div>
              <p className="text-sm leading-relaxed text-gray-600">
                {t('aiq.reportViewable')}
              </p>
            </>
          ) : isReportReady ? (
            <>
              <div className="flex justify-center items-center mb-4 w-14 h-14 bg-green-50/50 rounded-xl">
                <Loader2 className="w-7 h-7 text-green-400" />
              </div>
              <p className="text-sm leading-relaxed text-gray-600 whitespace-pre-line">
                {t('aiq.reportPreparingDesc')}
              </p>
            </>
          ) : (
            <>
              <div className="flex justify-center items-center mb-4 w-14 h-14 bg-gray-100 rounded-xl">
                <Lock className="w-7 h-7 text-gray-400" />
              </div>
              <p className="text-sm leading-relaxed text-gray-600 whitespace-pre-line">
                {t('aiq.reportLockedDesc')}
              </p>
            </>
          )}
        </div>

        {isAvailable ? (
          <button
            onClick={() => onNavigate(`/learn/${classProductId}/aiq/report`)}
            className="w-full mt-auto py-2.5 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            {t('aiq.viewReport')}
          </button>
        ) : (
          <button
            disabled
            className="w-full mt-auto py-2.5 px-4 bg-gray-100 text-gray-400 rounded-lg text-sm font-medium cursor-not-allowed"
          >
            {t('aiq.viewReport')}
          </button>
        )}
      </div>
    </div>
  )
}

function AIQInfoSection() {
  const { t } = useTranslation('learner')
  return (
    <div className="p-6 mt-12 bg-gray-50 rounded-xl">
      <h2 className="mb-3 text-lg font-semibold text-gray-900">{t('aiq.infoTitle')}</h2>
      <ul className="space-y-2 text-sm text-gray-600">
        <li className="flex gap-2 items-start">
          <span className="text-primary-600 mt-0.5">•</span>
          <span>{t('aiq.info1')}</span>
        </li>
        <li className="flex gap-2 items-start">
          <span className="text-primary-600 mt-0.5">•</span>
          <span>{t('aiq.info2')}</span>
        </li>
        <li className="flex gap-2 items-start">
          <span className="text-primary-600 mt-0.5">•</span>
          <span>{t('aiq.info3')}</span>
        </li>
        <li className="flex gap-2 items-start">
          <span className="text-primary-600 mt-0.5">•</span>
          <span>{t('aiq.info4')}</span>
        </li>
      </ul>
    </div>
  )
}

// ============================================================================
// Dev Tools Panel
// ============================================================================

interface AIQDevToolsPanelProps {
  productId: number
  classProductId: number
  bypassDate: boolean
  onToggleBypassDate: () => void
  bypassReportFlag: boolean
  onToggleBypassReportFlag: () => void
}

function AIQDevToolsPanel({ productId, classProductId, bypassDate, onToggleBypassDate, bypassReportFlag, onToggleBypassReportFlag }: AIQDevToolsPanelProps) {
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(false)
  const [isResetting, setIsResetting] = useState<string | null>(null)
  const [comparison, setComparison] = useState<{
    preQuizIds: string[]
    postQuizIds: string[]
    isSameSet: boolean
    onlyInPre: string[]
    onlyInPost: string[]
  } | null>(null)
  const [isComparing, setIsComparing] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const handleReset = async (type: 'pre' | 'post' | 'all') => {
    if (!confirm(`${type === 'all' ? 'Pre + Post' : type === 'pre' ? 'Pre' : 'Post'} AIQ를 리셋하시겠습니까?`)) return
    setIsResetting(type)
    setMessage(null)
    try {
      await aiqApi.resetAiq(productId, type, classProductId)
      queryClient.invalidateQueries({ queryKey: ['aiqStatus'] })
      queryClient.invalidateQueries({ queryKey: ['aiqStatusTransformed'] })
      queryClient.invalidateQueries({ queryKey: ['aiqStatuses'] })
      setMessage({ type: 'success', text: `${type} AIQ 리셋 완료` })
      setComparison(null)
    } catch (e: unknown) {
      setMessage({ type: 'error', text: `리셋 실패: ${e instanceof Error ? e.message : '알 수 없는 오류'}` })
    } finally {
      setIsResetting(null)
    }
  }

  const handleCompare = async () => {
    setIsComparing(true)
    setMessage(null)
    try {
      const res = await aiqApi.getQuizComparison(productId, classProductId)
      setComparison(res.data.data)
    } catch (e: unknown) {
      setMessage({ type: 'error', text: `비교 실패: ${e instanceof Error ? e.message : '알 수 없는 오류'}` })
    } finally {
      setIsComparing(false)
    }
  }

  return (
    <div className="mt-4 rounded-lg border-2 border-dashed border-orange-300 bg-orange-50/50">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex gap-2 justify-between items-center px-4 py-3 w-full text-sm font-medium text-orange-700"
      >
        <span className="flex gap-2 items-center">
          <Wrench className="w-4 h-4" />
          Dev Tools
        </span>
        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {isOpen && (
        <div className="px-4 pb-4 space-y-4">
          {/* Reset Buttons */}
          <div>
            <h4 className="mb-2 text-xs font-semibold tracking-wide text-orange-600 uppercase">AIQ 리셋</h4>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleReset('pre')}
                disabled={isResetting !== null}
                className="flex gap-1.5 items-center px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 disabled:opacity-50"
              >
                {isResetting === 'pre' ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                Pre-AIQ 리셋
              </button>
              <button
                onClick={() => handleReset('post')}
                disabled={isResetting !== null}
                className="flex gap-1.5 items-center px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-100 rounded-md hover:bg-purple-200 disabled:opacity-50"
              >
                {isResetting === 'post' ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                Post-AIQ 리셋
              </button>
              <button
                onClick={() => handleReset('all')}
                disabled={isResetting !== null}
                className="flex gap-1.5 items-center px-3 py-1.5 text-xs font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200 disabled:opacity-50"
              >
                {isResetting === 'all' ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                전체 리셋
              </button>
            </div>
          </div>

          {/* Date Bypass */}
          <div>
            <h4 className="mb-2 text-xs font-semibold tracking-wide text-orange-600 uppercase">Post-AIQ 날짜 제한</h4>
            <div className="flex gap-2 items-center">
              <button
                onClick={onToggleBypassDate}
                className={`flex gap-1.5 items-center px-3 py-1.5 text-xs font-medium rounded-md ${
                  bypassDate
                    ? 'text-orange-700 bg-orange-200 hover:bg-orange-300'
                    : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                }`}
              >
                <ShieldOff className="w-3 h-3" />
                {bypassDate ? '날짜 제한 무시 중' : '날짜 제한 무시하기'}
              </button>
              <span className="text-xs text-gray-500">
                (오픈일: 2026.04.23)
              </span>
            </div>
          </div>

          {/* Report Flag Bypass */}
          <div>
            <h4 className="mb-2 text-xs font-semibold tracking-wide text-orange-600 uppercase">리포트 잠금</h4>
            <div className="flex gap-2 items-center">
              <button
                onClick={onToggleBypassReportFlag}
                className={`flex gap-1.5 items-center px-3 py-1.5 text-xs font-medium rounded-md ${
                  bypassReportFlag
                    ? 'text-orange-700 bg-orange-200 hover:bg-orange-300'
                    : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                }`}
              >
                <ShieldOff className="w-3 h-3" />
                {bypassReportFlag ? '리포트 잠금 무시 중' : '리포트 잠금 무시하기'}
              </button>
              <span className="text-xs text-gray-500">
                (reportEnabled: false)
              </span>
            </div>
          </div>

          {/* Quiz Comparison */}
          <div>
            <h4 className="mb-2 text-xs font-semibold tracking-wide text-orange-600 uppercase">퀴즈 비교 (Pre vs Post)</h4>
            <button
              onClick={handleCompare}
              disabled={isComparing}
              className="flex gap-1.5 items-center px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 rounded-md hover:bg-green-200 disabled:opacity-50"
            >
              {isComparing ? <Loader2 className="w-3 h-3 animate-spin" /> : <GitCompare className="w-3 h-3" />}
              문제 비교하기
            </button>

            {comparison && (
              <div className="p-3 mt-2 space-y-2 text-xs bg-white rounded-md border">
                <div className="flex gap-2 items-center">
                  <span className="font-medium">동일 문제 세트:</span>
                  <span className={comparison.isSameSet ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>
                    {comparison.isSameSet ? 'YES' : 'NO'}
                  </span>
                </div>
                <div className="flex gap-4">
                  <span>Pre: {comparison.preQuizIds.length}문제</span>
                  <span>Post: {comparison.postQuizIds.length}문제</span>
                </div>
                {comparison.onlyInPre.length > 0 && (
                  <div className="text-orange-600">
                    Pre에만 있는 문제: {comparison.onlyInPre.join(', ')}
                  </div>
                )}
                {comparison.onlyInPost.length > 0 && (
                  <div className="text-orange-600">
                    Post에만 있는 문제: {comparison.onlyInPost.join(', ')}
                  </div>
                )}
                {comparison.preQuizIds.length === 0 && comparison.postQuizIds.length === 0 && (
                  <div className="text-gray-500">Pre/Post 세션 데이터가 없습니다.</div>
                )}
                <details className="mt-1">
                  <summary className="text-gray-500 cursor-pointer hover:text-gray-700">퀴즈 ID 상세</summary>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <div>
                      <div className="font-medium text-blue-600">Pre-AIQ</div>
                      <div className="font-mono text-gray-600 break-all">
                        {comparison.preQuizIds.length > 0
                          ? comparison.preQuizIds.map((id, i) => <div key={id}>{i + 1}. {id}</div>)
                          : <span className="text-gray-400">없음</span>}
                      </div>
                    </div>
                    <div>
                      <div className="font-medium text-purple-600">Post-AIQ</div>
                      <div className="font-mono text-gray-600 break-all">
                        {comparison.postQuizIds.length > 0
                          ? comparison.postQuizIds.map((id, i) => <div key={id}>{i + 1}. {id}</div>)
                          : <span className="text-gray-400">없음</span>}
                      </div>
                    </div>
                  </div>
                </details>
              </div>
            )}
          </div>

          {/* Status Message */}
          {message && (
            <div className={`text-xs px-3 py-2 rounded-md ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {message.text}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Main Page Component
// ============================================================================

export default function MyAIQ() {
  const { t } = useTranslation('learner')
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [selectedCourse, setSelectedCourse] = useState<number | null>(null)
  const [bypassDate, setBypassDate] = useState(false)
  const [bypassReportFlag, setBypassReportFlag] = useState(false)
  const [showVideoGateModal, setShowVideoGateModal] = useState(false)

  // Fetch user's class courses (same format as MyCourses.tsx for cache sharing)
  const { data: coursesData, isLoading: isLoadingCourses } = useQuery({
    queryKey: ['myClassCourses'],
    queryFn: () => learnerApi.getMyClassCourses(),
    staleTime: 5 * 60 * 1000, // 5분간 캐시 유지 (페이지 이동 시 재요청 방지)
    gcTime: 10 * 60 * 1000, // 10분간 가비지 컬렉션 방지
  })

  const courses = coursesData?.data?.data || []

  // Auto-select first course when courses are loaded
  useEffect(() => {
    if (courses.length > 0 && selectedCourse === null) {
      setSelectedCourse(courses[0].classProductId)
    }
  }, [courses, selectedCourse])

  // Current course ID and data
  const currentCourseId = selectedCourse
  const currentCourse = courses.find(c => c.classProductId === currentCourseId)

  // Fetch AIQ status for selected course (transformed for MyAIQ page)
  const { data: aiqData, isLoading: isLoadingAIQ, error } = useQuery<AIQStatus | null>({
    queryKey: ['aiqStatusTransformed', currentCourse?.productId, currentCourseId],
    queryFn: async () => {
      if (!currentCourse?.productId || !currentCourseId) return null
      const response = await aiqApi.getStatus(currentCourse.productId, currentCourseId)
      // Transform backend response to frontend format
      return aiqApi.transformToFrontendStatus(
        response.data.data,
        currentCourse.productId,
        currentCourseId
      )
    },
    enabled: !!currentCourse?.productId && !!currentCourseId,
  })

  // Configuration 조회 (영상 활동 설정 확인용)
  const { data: configData } = useQuery({
    queryKey: ['classConfig', currentCourse?.productId, currentCourse?.classId],
    queryFn: () => getEffectiveConfigurationForB2B({
      productId: currentCourse!.productId,
      classId: currentCourse!.classId,
    }),
    enabled: !!currentCourse?.productId,
    staleTime: 10 * 60 * 1000,
  })

  const cfgStartActivity = configData?.configuration?.productLearning?.startActivity
  const hasStartVideo = cfgStartActivity?.enabled === true && (cfgStartActivity?.types?.includes('video') ?? false)

  // 영상 활동 상태 조회
  const { data: videoActivityStatus } = useQuery({
    queryKey: ['productVideoActivity', currentCourse?.productId, currentCourseId],
    queryFn: async () => {
      const res = await productVideoActivityApi.getStatus(currentCourse!.productId, currentCourseId!)
      return res.data.data
    },
    enabled: hasStartVideo && !!currentCourse?.productId && !!currentCourseId,
    staleTime: 5 * 60 * 1000,
  })

  const isVideoGated = hasStartVideo && videoActivityStatus?.startVideo?.status !== 'completed'

  const handleNavigate = (path: string) => {
    navigate(path)
  }

  // Loading state
  if (isLoadingCourses) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 w-8 h-8 animate-spin text-primary-600" />
          <p className="text-gray-600">{t('aiq.loading')}</p>
        </div>
      </div>
    )
  }

  // No courses
  if (!courses.length) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="text-center">
          <Brain className="mx-auto mb-4 w-16 h-16 text-gray-400" />
          <p className="text-gray-600">{t('aiq.noCourses')}</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-4 w-16 h-16 text-red-400" />
          <p className="text-gray-600">{t('aiq.loadError')}</p>
          <p className="mt-2 text-sm text-gray-500">{t('aiq.retryLater')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex gap-3 items-center mb-2">
          <img src="/aiq-logo.png" alt="AIQ" className="h-8" />
          <h1 className="text-3xl font-bold text-gray-900">{t('aiq.title')}</h1>
        </div>
        <p className="text-gray-500">{t('aiq.subtitle')}</p>
      </div>

      {/* Course Selector (if multiple courses) */}
      {courses.length > 1 && (
        <div className="p-4 bg-white rounded-lg shadow-sm">
          <label htmlFor="course-select" className="block mb-2 text-sm font-medium text-gray-700">
            {t('aiq.selectCourse')}
          </label>
          <select
            id="course-select"
            value={currentCourseId || ''}
            onChange={(e) => setSelectedCourse(Number(e.target.value))}
            className="px-3 py-2 w-full rounded-lg border border-gray-300 md:w-96 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {courses.map((course) => (
              <option key={course.classProductId} value={course.classProductId}>
                {course.productName || course.contentTitle || t('aiq.noTitle')}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Loading AIQ data (also show when waiting for course selection) */}
      {(isLoadingAIQ || (courses.length > 0 && !currentCourseId)) && (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      )}

      {/* Cards Grid */}
      {aiqData && currentCourseId && (aiqData.preAiqEnabled || aiqData.postAiqEnabled) && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {aiqData.preAiqEnabled && (
            <PreAIQCard
              preTest={aiqData.preTest}
              classProductId={currentCourseId}
              onNavigate={handleNavigate}
              isVideoGated={isVideoGated}
              onVideoGateBlocked={() => setShowVideoGateModal(true)}
            />
          )}
          {aiqData.postAiqEnabled && (
            <PostAIQCard
              postTest={aiqData.postTest}
              classProductId={currentCourseId}
              onNavigate={handleNavigate}
              bypassDate={bypassDate}
            />
          )}
          {aiqData.preAiqEnabled && aiqData.postAiqEnabled && (
            <AIQReportCard
              report={aiqData.report}
              classProductId={currentCourseId}
              onNavigate={handleNavigate}
              bypassReportFlag={bypassReportFlag}
            />
          )}
        </div>
      )}

      {/* AIQ 미설정 안내 */}
      {aiqData && currentCourseId && !aiqData.preAiqEnabled && !aiqData.postAiqEnabled && (
        <div className="flex flex-col justify-center items-center py-16">
          <Brain className="mb-4 w-16 h-16 text-gray-300" />
          <p className="text-lg font-medium text-gray-500">{t('aiq.notConfigured')}</p>
          <p className="mt-1 text-sm text-gray-400">{t('aiq.notConfiguredDesc')}</p>
        </div>
      )}

      {/* Info Section */}
      <AIQInfoSection />

      {/* Dev Tools */}
      {isDevToolsVisible(user?.email) && currentCourse && currentCourseId && (
        <AIQDevToolsPanel
          productId={currentCourse.productId}
          classProductId={currentCourseId}
          bypassDate={bypassDate}
          onToggleBypassDate={() => setBypassDate(prev => !prev)}
          bypassReportFlag={bypassReportFlag}
          onToggleBypassReportFlag={() => setBypassReportFlag(prev => !prev)}
        />
      )}

      {/* Video Gate Modal */}
      <Dialog open={showVideoGateModal} onOpenChange={setShowVideoGateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('courses.startVideoRequired')}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-6 items-center py-4 text-center">
            <img src={aiqBiSvg} alt="AIQ" className="h-10" />
            <p className="text-sm text-gray-600 whitespace-pre-line">{t('courses.startVideoRequiredDesc', { name: user?.fullName })}</p>
            <button
              onClick={() => {
                setShowVideoGateModal(false)
                navigate('/my/courses')
              }}
              className="px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-colors bg-primary-600 hover:bg-primary-700"
            >
              {t('courses.goToCourses')}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
